import { ReactElement, useCallback, useState, ReactNode } from "react";
import { Grid, Skeleton, Button, Badge, SvgIcon, Accordion, AccordionSummary, AccordionDetails, Typography, useTheme, darken } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";

import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CalendarViewMonthIcon from "@mui/icons-material/CalendarViewMonth";
import DehazeIcon from "@mui/icons-material/Dehaze";

import TitledCard, { TitledCardMode } from "../TitledCard";
import { DisplayZoneValue, ZonesPlistValue, Partition } from "./types";
import PZConfigurator, { BootstrapTooltip, MatrixValue, PZConfiguratorValue } from "./PZConfigurator";
import { ZoneTriggers } from "./types";
import { triggers, behaviors } from "./SmartalarmBoard";
import ZoneChip from "../ZoneView";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import NotificationsOffOutlinedIcon from "@mui/icons-material/NotificationsOffOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

interface PartitionsAndZonesProps {
  partitions: Partition[];
  zones: DisplayZoneValue[];
  value: PZConfiguratorValue | null;
  saved: ZonesPlistValue | null;
  onChange: (value: PZConfiguratorValue) => void;
  disabled?: boolean;
}

const AlarmOutAccordion = ({
  zone,
  siblings,
  zones,
  summary: _summary,
  emptyMessage,
}: {
  zone: DisplayZoneValue | null;
  siblings: string[];
  zones: DisplayZoneValue[];
  summary?: ReactElement;
  emptyMessage?: ReactNode;
}) => {
  const trigger = triggers.find((trigger) => trigger.value === zone?.trigger);

  const siblingZones = siblings
    .map((sibling) => zones.find((z) => z.uuid === sibling))
    .filter((zone) => !zone?.output)
    .sort((a, b) => {
      if (a && b) {
        return a?.code.localeCompare(b?.code);
      } else if (a) {
        return -1;
      }
      return 1;
    });

  const siblingZoneChips = siblingZones.map((zone) => {
    const behavior = behaviors.find((behavior) => behavior.value === zone?.behavior?.toString(2));
    return (
      zone && (
        <Grid item sx={{ justifyContent: "center" }} key={zone.uuid}>
          <ZoneChip zone={zone} icon={behavior?.icon} zoneColor={behavior?.color} />
        </Grid>
      )
    );
  });

  const summary = _summary || (
    <>
      <SvgIcon sx={[{ color: trigger?.color, m: 1 }]} fontSize="small">
        {trigger?.icon}
      </SvgIcon>
      <Typography variant="subtitle2" sx={{ alignSelf: "center", mr: 1 }}>
        {zone?.code}
      </Typography>
      <Typography variant="subtitle2" sx={{ alignSelf: "center" }}>
        {zone?.name}
      </Typography>
    </>
  );

  return (
    <Accordion disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header" sx={{ alignItems: "center" }}>
        {summary}
        <div style={{ flex: 1 }} />
        <Typography variant="subtitle2" sx={{ alignSelf: "center", justifySelf: "flex-end" }}>
          {siblingZones.length}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container>
          {siblingZoneChips.length > 0 ? siblingZoneChips : emptyMessage || <FormattedMessage defaultMessage={"No zone triggers this output"} />}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

const PartitionsAndZones = ({ partitions, zones, value, saved, disabled, onChange }: PartitionsAndZonesProps) => {
  const [switchPartitionsAndZones, setSwitchPartitionsAndZones] = useState(true);
  const [view, setView] = useState<"grid" | "row">("grid");
  const [matrix, setMatrix] = useState<MatrixValue[][] | null>(null);
  const theme = useTheme();
  const { formatMessage } = useIntl();

  const onValueChange = useCallback(
    (newValue: MatrixValue) => {
      if (!value) {
        return;
      }
      const { zId, pId, value: v } = newValue;
      value[zId][pId] = v;
      onChange(value);
    },
    [value, onChange]
  );

  const onAllValueChange = useCallback(
    (newValue: MatrixValue[][]) => {
      if (!value) {
        return;
      }
      newValue.forEach((zones) => {
        zones.forEach(({ zId, pId, value: val }) => {
          value[zId][pId] = val;
        });
      });
      onChange(value);
    },
    [value, onChange]
  );

  if (!value || !partitions || !zones) {
    return (
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <TitledCard title={formatMessage({ defaultMessage: "Zone & partition assignments" })}>
            <Grid container spacing={1} sx={{ padding: 1 }}>
              <Grid item xs={12}>
                <Skeleton variant="text" width={110} />
              </Grid>
              <Grid item>
                <Skeleton variant="rectangular" height={32} width={100} sx={{ borderRadius: 16 }} />
              </Grid>
              <Grid item>
                <Skeleton variant="rectangular" height={32} width={100} sx={{ borderRadius: 16 }} />
              </Grid>
              <Grid item xs={12}>
                <Skeleton variant="text" width={110} />
              </Grid>
              <Grid item>
                <Skeleton variant="rectangular" height={32} width={100} sx={{ borderRadius: 16 }} />
              </Grid>
              <Grid item>
                <Skeleton variant="rectangular" height={32} width={100} sx={{ borderRadius: 16 }} />
              </Grid>
              <Grid item>
                <Skeleton variant="rectangular" height={32} width={100} sx={{ borderRadius: 16 }} />
              </Grid>
            </Grid>
          </TitledCard>
        </Grid>
      </Grid>
    );
  }

  const alarmOutZonesWithSiblings = zones.reduce((acc, curr, currIndex) => {
    if (curr.output && curr.trigger === "alarm" && !curr.deleted && matrix) {
      const siblings = matrix[currIndex].reduce((siblingZoneIds: Set<string>, curr: MatrixValue) => {
        const { pId, zId, value: inPartition } = curr;

        matrix.forEach((matrixValue) => {
          if (inPartition) {
            if (matrixValue.some((value) => value.pId === pId && value.value && value.zId !== zId)) {
              siblingZoneIds.add(matrixValue[0].zId);
            }
          }
        });

        return siblingZoneIds;
      }, new Set<string>());

      acc.push({
        zone: curr,
        index: currIndex,
        siblings: Array.from(siblings),
      });
    }
    return acc;
  }, [] as { zone: DisplayZoneValue; index: number; siblings: string[] }[]);

  const alarmTrigger = triggers.find((t) => t.value === ZoneTriggers.alarm);

  const allAlarmOutSiblings = alarmOutZonesWithSiblings?.reduce((acc, { siblings }) => {
    siblings.forEach((s) => acc.add(s));
    return acc;
  }, new Set());

  const zonesWithoutAlarmOutSiblings = zones?.reduce((acc: string[], curr) => {
    if (!allAlarmOutSiblings.has(curr.uuid)) {
      acc.push(curr.uuid);
    }
    return acc;
  }, []);

  return (
    <>
      <TitledCard
        title={formatMessage({ defaultMessage: "Zone & partition assignments" })}
        modes={
          [
            {
              icon: (
                <Badge
                  badgeContent={disabled ? <RemoveRedEyeOutlinedIcon fontSize="inherit" /> : <EditOutlinedIcon fontSize="inherit" />}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                >
                  <CalendarViewMonthIcon fontSize="small" />
                </Badge>
              ),
              content: null,
            },
            {
              icon: (
                <Badge
                  badgeContent={<EditOutlinedIcon fontSize="inherit" />}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                >
                  <DehazeIcon fontSize="small" />
                </Badge>
              ),
              content: null,
            },
          ] as TitledCardMode[]
        }
        onModeChange={(m: number) => {
          setView(m === 0 ? "grid" : "row");
        }}
      >
        <Grid container spacing={1} sx={{ display: "flex", flexDirection: "column" }}>
          <Grid item xs={12} sx={{ margin: 1 }}>
            <Button startIcon={<CompareArrowsIcon />} onClick={() => setSwitchPartitionsAndZones((old) => !old)}>
              {formatMessage({ defaultMessage: "Switch partitions and zones" })}
            </Button>
          </Grid>
          <Grid item xs={12} sx={{ flex: 1 }}>
            <PZConfigurator
              value={value}
              matrix={matrix}
              setMatrix={setMatrix}
              saved={saved}
              partitions={partitions}
              zones={zones}
              switchPartitionsAndZones={switchPartitionsAndZones}
              view={view}
              onChange={onValueChange}
              onAllChange={onAllValueChange}
              disabled={disabled}
            />
          </Grid>
        </Grid>
      </TitledCard>
      <Grid container spacing={1}>
        <Grid item lg={6} md={8} sm={10} xs={12}>
          <TitledCard
            title={
              <div style={{ display: "flex", alignItems: "center" }}>
                <FormattedMessage defaultMessage={"Loud outputs"} />
                <BootstrapTooltip title={<FormattedMessage defaultMessage={"Outputs activated by zone alert"} />} backgroundcolor={theme.palette.error.main}>
                  <SvgIcon fontSize="small" sx={{ ml: 1 }}>
                    <InfoOutlinedIcon />
                  </SvgIcon>
                </BootstrapTooltip>
              </div>
            }
            style={{ marginTop: theme.spacing(1) }}
            headerSx={{ backgroundColor: alarmTrigger?.color }}
            headerIcon={<SvgIcon sx={{ color: "common.white" }}>{alarmTrigger?.icon}</SvgIcon>}
          >
            {alarmOutZonesWithSiblings.map(({ zone, siblings }) => (
              <AlarmOutAccordion key={zone.uuid} zone={zone} siblings={siblings} zones={zones} />
            ))}
            <AlarmOutAccordion
              zone={null}
              siblings={zonesWithoutAlarmOutSiblings}
              zones={zones}
              emptyMessage={<FormattedMessage defaultMessage={"All zones connected to a loud output"} />}
              summary={
                <div style={{ display: "flex", alignItems: "center" }}>
                  <SvgIcon sx={[{ color: theme.palette.divider, m: 1 }]} fontSize="small">
                    <NotificationsOffOutlinedIcon />
                  </SvgIcon>
                  <Typography variant="subtitle2" sx={{ alignSelf: "center", mr: 1 }}>
                    <FormattedMessage defaultMessage={"Silent zones"} />
                  </Typography>
                  <BootstrapTooltip
                    title={
                      <Typography variant="caption" sx={{ alignSelf: "center" }}>
                        <FormattedMessage defaultMessage={"Zones that have no effect on any loud output"} />
                      </Typography>
                    }
                    backgroundcolor={darken(theme.palette.divider, 0.5)}
                  >
                    <SvgIcon fontSize="small" sx={{ ml: 1 }}>
                      <InfoOutlinedIcon />
                    </SvgIcon>
                  </BootstrapTooltip>
                </div>
              }
            />
          </TitledCard>
        </Grid>
      </Grid>
    </>
  );
};

export default PartitionsAndZones;
